import React, { useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const topNavLinkClass = ({ isActive }) =>
    [
      'inline-flex items-center px-1 pt-1 font-medium border-b-2 transition-colors',
      isActive
        ? 'text-honey-600 border-honey-500'
        : 'text-slate-900 border-transparent hover:text-slate-700',
    ].join(' ');

  const mobileNavLinkClass = ({ isActive }) =>
    [
      'block px-3 py-2 rounded-lg font-medium transition-colors',
      isActive ? 'bg-honey-50 text-honey-700' : 'text-slate-900 hover:bg-slate-100',
    ].join(' ');

  const handleAdhesionClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Si on est déjà sur la page d'accueil, on scroll vers la section
      const element = document.getElementById('adhesion');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Sinon, on navigue vers la page d'accueil avec le hash
      navigate('/#adhesion');
    }
  };

  return (
    <nav className="mx-auto pt-10" >
      <div className="min-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{zIndex: 999}}>
        <div className="flex bg-white bg-opacity-60 rounded-xl p-6 justify-between shadow-soft">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-md font-extrabold text-honey-600 uppercase">
                <img src="/logo.png" alt="Logo Apiculture" className="w-auto h-16" />
              </Link>
            </div>
            <div className="hidden sm:ml-12 sm:flex sm:space-x-8">
              <NavLink to="/" end className={topNavLinkClass}>
                Accueil
              </NavLink>
              <NavLink to="/actualites" className={topNavLinkClass}>
                Actualités
              </NavLink>
              {user?.role === 'admin' || user?.role === 'super_admin' ? (
                <NavLink to="/adhesions" className={topNavLinkClass}>
                  Adhérents
                </NavLink>
              ) : user ? (
                <>
                  <NavLink to="/qui-sommes-nous" className={topNavLinkClass}>
                    Qui sommes-nous
                  </NavLink>
                  <NavLink to="/dashboard" className={topNavLinkClass}>
                    Espace personnel
                  </NavLink>
                </>
              ) : (
                <NavLink to="/qui-sommes-nous" className={topNavLinkClass}>
                  Qui sommes-nous ?
                </NavLink>
              )}
            </div>
          </div>
          {/* Bouton hamburger mobile */}
          <div className="flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-slate-700 hover:text-slate-900 hover:bg-slate-100 focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Ouvrir le menu</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>

          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 font-medium text-slate-700 hover:text-slate-900 focus:outline-none"
                >
                  <span>{user.prenom} {user.nom}</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-10">
                    {!['admin', 'super_admin'].includes(user.role) ? (
                      <>
                        <Link
                          to="/dashboard"
                          className="block px-4 py-2 text-slate-700 hover:bg-slate-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Espace personnel
                        </Link>
                        <Link
                          to="/profil"
                          className="block px-4 py-2 text-slate-700 hover:bg-slate-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Mon profil
                        </Link>
                        <Link
                          to="/securite"
                          className="block px-4 py-2 text-slate-700 hover:bg-slate-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Sécurité
                        </Link>
                        <Link
                          to="/adhesion"
                          className="block px-4 py-2 text-slate-700 hover:bg-slate-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Nouvelle adhésion
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/adhesions"
                          className="block px-4 py-2 text-slate-700 hover:bg-slate-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Adhérents
                        </Link>
                        <Link
                          to="/securite"
                          className="block px-4 py-2 text-slate-700 hover:bg-slate-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Sécurité
                        </Link>
                      </>
                    )}
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-100"
                    >
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-8">
                <Link
                  to="/login"
                  className="font-medium text-slate-500 hover:text-slate-900"
                >
                  Votre espace personnel
                </Link>
                <button
                  onClick={handleAdhesionClick}
                  className="inline-flex items-center px-4 py-2 border border-transparent font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600"
                >
                  Adhérer à un groupement
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Menu mobile */}
        {mobileMenuOpen && (
          <div className="sm:hidden mt-2 bg-white bg-opacity-95 rounded-xl shadow-lg p-4">
            <div className="space-y-2">
              <NavLink
                to="/"
                end
                className={mobileNavLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                Accueil
              </NavLink>
              <NavLink
                to="/actualites"
                className={mobileNavLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                Actualités
              </NavLink>
              <NavLink
                to="/qui-sommes-nous"
                className={mobileNavLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                Qui sommes-nous ?
              </NavLink>
              {user ? (
                <>
                  {!['admin', 'super_admin'].includes(user.role) ? (
                    <>
                      <NavLink
                        to="/dashboard"
                        className={mobileNavLinkClass}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Espace personnel
                      </NavLink>
                      <NavLink
                        to="/profil"
                        className={mobileNavLinkClass}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Mon profil
                      </NavLink>
                      <NavLink
                        to="/adhesion"
                        className={mobileNavLinkClass}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Nouvelle adhésion
                      </NavLink>
                    </>
                  ) : (
                    <NavLink
                      to="/adhesions"
                      className={mobileNavLinkClass}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Adhérents
                    </NavLink>
                  )}
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <div className="px-3 py-2 text-sm text-slate-500">
                      {user.prenom} {user.nom}
                    </div>
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-3 py-2 rounded-lg text-red-600 font-medium hover:bg-red-50"
                    >
                      Déconnexion
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 rounded-lg text-slate-900 font-medium hover:bg-slate-100"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Votre espace personnel
                  </Link>
                  <button
                    onClick={(e) => {
                      handleAdhesionClick(e);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full mt-2 px-4 py-2 bg-honey-500 text-white font-medium rounded-lg hover:bg-honey-600"
                  >
                    Adhérer à un groupement
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
